import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  query,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { firestore } from '../views/firebaseConfig';
import { COLLECTIONS, text } from '../contracts/p4r';

export const GENERAL_REPORT_REASONS = Object.freeze([
  'Violence, Gore, Blood',
  'Spam, Scam Fear',
  'Nudity, Obscene',
  'Disturbing',
  'Harassment or Hate',
  'Other',
]);

export const LISTING_REPORT_REASONS = Object.freeze([
  'Prohibited or unsafe item',
  'Misleading listing',
  'Spam or scam',
  'Counterfeit item',
  'Harassment or abuse',
  'Other',
]);

const ensureSignedIn = (uid) => {
  if (!uid) throw new Error('You must be signed in to complete this action.');
};

async function hasExistingReport(collectionName, uid, idField, targetId) {
  const q = query(
    collection(firestore, collectionName),
    where('reportedBy', '==', uid),
    where(idField, '==', targetId),
    limit(1),
  );
  const snapshot = await getDocs(q);
  return !snapshot.empty;
}

export async function reportFeedPost({ uid, post, reason, otherReason = '' }) {
  ensureSignedIn(uid);
  if (!post?.id) throw new Error('This post is missing its identifier.');
  if (!text(reason)) throw new Error('Select a reason for reporting.');
  if (reason === 'Other' && !text(otherReason)) throw new Error('Describe the reason for this report.');
  if (await hasExistingReport(COLLECTIONS.FEED_REPORTS, uid, 'postId', post.id)) {
    throw new Error('You already reported this post.');
  }

  await addDoc(collection(firestore, COLLECTIONS.FEED_REPORTS), {
    timestamp: serverTimestamp(),
    reportedBy: uid,
    postId: post.id,
    origin: text(post.userId),
    reason: text(reason),
    otherReason: reason === 'Other' ? text(otherReason) : '',
    status: 'Under Review',
    result: 'Pending',
  });

  await setDoc(doc(firestore, COLLECTIONS.FEED_EXPERIENCE, uid), {
    reportedPosts: arrayUnion(post.id),
    [`reportedUsers.${text(post.userId)}`]: increment(1),
    [`reportedCategoryCounts.${text(post.category || 'Uncategorized')}`]: increment(1),
    timeUpdated: serverTimestamp(),
  }, { merge: true });
}

export async function reportListing({ uid, listingId, reason, otherReason = '' }) {
  ensureSignedIn(uid);
  if (!listingId) throw new Error('This listing is missing its identifier.');
  if (!text(reason)) throw new Error('Select a reason for reporting.');
  if (reason === 'Other' && !text(otherReason)) throw new Error('Describe the reason for this report.');
  if (await hasExistingReport(COLLECTIONS.STORE_REPORTS, uid, 'forsaleId', listingId)) {
    throw new Error('You already reported this listing.');
  }

  const reportRef = doc(collection(firestore, COLLECTIONS.STORE_REPORTS));
  await setDoc(reportRef, {
    reportId: reportRef.id,
    timestamp: serverTimestamp(),
    reportedBy: uid,
    forsaleId: listingId,
    reason: text(reason),
    otherReason: reason === 'Other' ? text(otherReason) : '',
    status: 'Under Review',
    result: 'Not ready yet',
  });
}

export async function reportAdvertisement({ uid, purchaseId, reason, otherReason = '' }) {
  ensureSignedIn(uid);
  if (!purchaseId) throw new Error('This advertisement is missing its purchase identifier.');
  if (!text(reason)) throw new Error('Select a reason for reporting.');
  if (reason === 'Other' && !text(otherReason)) throw new Error('Describe the reason for this report.');
  if (await hasExistingReport(COLLECTIONS.AD_REPORTS, uid, 'purchaseId', purchaseId)) {
    throw new Error('You already reported this advertisement.');
  }

  const reportRef = doc(collection(firestore, COLLECTIONS.AD_REPORTS));
  await setDoc(reportRef, {
    reportId: reportRef.id,
    timestamp: serverTimestamp(),
    reportedBy: uid,
    purchaseId,
    reason: text(reason),
    otherReason: reason === 'Other' ? text(otherReason) : '',
    status: 'Under Review',
    result: 'Pending',
  });
}

export async function blockUser({ uid, targetUid, conversationId = '' }) {
  ensureSignedIn(uid);
  if (!targetUid || targetUid === uid) throw new Error('Choose another user to block.');

  const batch = writeBatch(firestore);
  batch.set(doc(firestore, COLLECTIONS.BLOCKED_USERS, uid), {
    blockedList: arrayUnion(targetUid),
    timeUpdated: serverTimestamp(),
  }, { merge: true });
  batch.set(doc(firestore, COLLECTIONS.BLOCKED_BY, targetUid), {
    blockedByUsers: arrayUnion(uid),
    timeUpdated: serverTimestamp(),
  }, { merge: true });
  if (conversationId) {
    batch.set(doc(firestore, COLLECTIONS.INBOX, conversationId), {
      blockedConversation: true,
      blockComms: uid,
      timeUpdated: serverTimestamp(),
    }, { merge: true });
  }
  await batch.commit();
}


export async function getOwnReports(uid) {
  ensureSignedIn(uid);
  const sources = [
    [COLLECTIONS.FEED_REPORTS, 'Pockit report'],
    [COLLECTIONS.STORE_REPORTS, 'Shoparound report'],
    [COLLECTIONS.AD_REPORTS, 'Advertisement report'],
    [COLLECTIONS.REPORTS, 'Account report'],
  ];
  const groups = await Promise.all(sources.map(async ([collectionName, type]) => {
    const snapshot = await getDocs(query(collection(firestore, collectionName), where('reportedBy', '==', uid), limit(100)));
    return snapshot.docs.map((item) => ({ id: item.id, type, collectionName, ...item.data() }));
  }));
  return groups.flat().sort((a, b) => {
    const aTime = a.timestamp?.toMillis?.() || a.timestamp?.seconds * 1000 || 0;
    const bTime = b.timestamp?.toMillis?.() || b.timestamp?.seconds * 1000 || 0;
    return bTime - aTime;
  });
}

export async function getBlockedProfiles(uid) {
  ensureSignedIn(uid);
  const snapshot = await getDoc(doc(firestore, COLLECTIONS.BLOCKED_USERS, uid));
  const blockedIds = Array.isArray(snapshot.data()?.blockedList) ? snapshot.data().blockedList : [];
  const profiles = await Promise.all(blockedIds.map(async (targetUid) => {
    const profile = await getDoc(doc(firestore, COLLECTIONS.USER_PROFILES, targetUid));
    return { id: targetUid, ...(profile.exists() ? profile.data() : {}) };
  }));
  return profiles;
}

export async function unblockUser({ uid, targetUid }) {
  ensureSignedIn(uid);
  if (!targetUid) return;
  const batch = writeBatch(firestore);
  batch.set(doc(firestore, COLLECTIONS.BLOCKED_USERS, uid), {
    blockedList: arrayRemove(targetUid),
    timeUpdated: serverTimestamp(),
  }, { merge: true });
  batch.set(doc(firestore, COLLECTIONS.BLOCKED_BY, targetUid), {
    blockedByUsers: arrayRemove(uid),
    timeUpdated: serverTimestamp(),
  }, { merge: true });
  await batch.commit();
}
